import { el } from '@/ui/hud';

/**
 * The share preview, for a browser with no system share sheet (a desk,
 * mostly): the story card scaled to fit, the text to copy, the link, and
 * Copy link / Download image / Copy text — Blockari's `SharePreview` in the
 * kit's dialog. Mounted on the body while it is up, above everything.
 */

export interface SharePreviewInput {
  story: Blob;
  text: string;
  url: string;
  /** True when `url` is a `/s/:id` link that unfurls into the card. */
  unfurls: boolean;
  /** The dialog's title; "Share your score" unless it is something else being shared. */
  heading?: string;
}

const DOWNLOAD_NAME = '21wild-score.jpg';

const ICON_COPY =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="12" height="12" rx="2"></rect><path d="M5 15V5a2 2 0 0 1 2-2h10"></path></svg>';
const ICON_DOWNLOAD =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v12"></path><path d="M8 11l4 4 4-4"></path><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"></path></svg>';

/** A button that copies `value` and says so for a moment. */
function copyButton(label: string, value: string, cls: string): HTMLButtonElement {
  const button = el('button', cls) as HTMLButtonElement;
  button.type = 'button';
  const paint = (text: string): void => {
    button.innerHTML = ICON_COPY;
    button.append(document.createTextNode(text));
  };
  paint(label);
  button.addEventListener('click', () => {
    void navigator.clipboard
      ?.writeText(value)
      .then(() => {
        paint('Copied');
        button.dataset.done = 'true';
      })
      .catch(() => paint("Couldn't copy"))
      .finally(() => {
        window.setTimeout(() => {
          paint(label);
          delete button.dataset.done;
        }, 1800);
      });
  });
  return button;
}

/** Show the preview; resolves when it is put away. */
export function showSharePreview(input: SharePreviewInput): Promise<void> {
  return new Promise((resolve) => {
    const src = URL.createObjectURL(input.story);
    const overlay = el('div', 'w21-share-overlay w21-overlay');
    const dialog = el('div', 'w21-dialog w21-share-dialog');
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-labelledby', 'w21-share-title');
    const title = el('h2', undefined, input.heading ?? 'Share your score');
    title.id = 'w21-share-title';

    const preview = el('div', 'w21-share-preview');
    const img = el('img') as HTMLImageElement;
    img.src = src;
    img.alt = 'Your share card';
    preview.append(img);

    const text = el('pre', 'w21-share-text', input.text);
    const link = el('div', 'w21-share-link');
    // The card prints the take link; this one is the share link — said once, here.
    link.append(
      el('span', 'w21-t-label', input.unfurls ? 'Link (unfurls into this card)' : 'Link'),
      el('code', undefined, input.url),
    );

    const actions = el('div', 'w21-actions');
    const split = el('div', 'w21-row w21-split');
    const download = el('a', 'w21-btn') as HTMLAnchorElement;
    download.href = src;
    download.download = DOWNLOAD_NAME;
    download.innerHTML = ICON_DOWNLOAD;
    download.append(document.createTextNode('Download image'));
    split.append(copyButton('Copy link', input.url, 'w21-btn w21-primary'), download);
    const lede = el(
      'p',
      'w21-lede',
      input.unfurls
        ? 'Paste the link anywhere — it unfurls into this card.'
        : 'Post the image with the link — it opens 21 Wild.',
    );
    const done = el('button', 'w21-btn w21-ghost w21-block', 'Done') as HTMLButtonElement;
    done.type = 'button';
    actions.append(split, copyButton('Copy text', `${input.text}\n${input.url}`, 'w21-btn w21-block w21-ghost'), lede, done);

    dialog.append(title, preview, text, link, actions);
    overlay.append(dialog);

    const close = (): void => {
      window.removeEventListener('keydown', onKey);
      overlay.remove();
      URL.revokeObjectURL(src);
      resolve();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close();
    };
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) close();
    });
    done.addEventListener('click', close);
    window.addEventListener('keydown', onKey);
    document.body.append(overlay);
    done.focus();
  });
}
